/**
 * What the lesson menu ("All Lessons and Quizzes") shows, kept apart from the overlay so it can be tested
 * (Sol ui-leftovers G3: "Use Resume for the current lesson, make Start Quiz secondary, widen the web modal, and make
 * all cards fully scrollable").
 */

export const RESUME_LABEL = 'Resume';
export const START_LESSON_LABEL = 'Start Lesson';

/** The widest the menu gets: the web player is desktop-only, so titles get room; native keeps its phone width. */
export const MENU_MAX_WIDTH_WEB = 560;
export const MENU_MAX_WIDTH_NATIVE = 320;

const clampPct = value => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

/**
 * The lesson that carries the NOW badge: the lesson the learner is in (the latest start_lesson, or the persisted
 * current lesson), or - when nothing has been started yet - the first lesson whose quiz is not at 100%.
 */
export function pickNowLessonId({ activeLessonId = null, lessons = [], quizMasteryPct = () => 0 } = {}) {
  if (activeLessonId != null) return activeLessonId;
  const firstNotFullyMastered = lessons.find(lesson => quizMasteryPct(lesson) < 100);
  return firstNotFullyMastered?.lessonId ?? null;
}

/**
 * The primary action's label. The NOW lesson, part-way through, reads "Resume" (it continues from its slide);
 * every other lesson - including a finished one, which restarts from the first slide - keeps "Start Lesson".
 */
export function lessonActionLabel({ isCurrent = false, progress = 0 } = {}) {
  const pct = clampPct(progress);
  return isCurrent && pct > 0 && pct < 100 ? RESUME_LABEL : START_LESSON_LABEL;
}

/**
 * Card and list sizes. `areaHeight` is the height the overlay actually has (falls back to the window), so the card
 * always fits inside it with a gutter; the list gets whatever the header leaves, minus the card's 1 px borders, and
 * scrolls - so the last lesson's buttons can always be scrolled into view above the card's bottom edge.
 */
export function lessonMenuSize({ width = 0, height = 0, areaHeight = 0, headerHeight = 0, gutter = 24, isWeb = false } = {}) {
  const maxWidth = isWeb ? MENU_MAX_WIDTH_WEB : MENU_MAX_WIDTH_NATIVE;
  const cardWidth = Math.max(0, Math.min(maxWidth, width - gutter * 2));
  const available = areaHeight > 0 ? areaHeight : height;
  const cardMaxHeight = Math.max(0, Math.round(Math.min(available * 0.8, available - gutter * 2)));
  const borders = 2;
  const listMaxHeight = Math.max(120, cardMaxHeight - Math.max(0, headerHeight) - borders);
  return { cardWidth, cardMaxHeight, listMaxHeight };
}
